import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import {
  Badge,
  Button,
  DrawerSection,
  SlideOut,
  SlideOutHeader,
} from '@aimsos/ui';

import { widgetTypeIcon } from '../constants/widgetTypeIcons.js';
import { widgetTypeCopyKeys } from '../constants/widgetTypeCopy.js';
import { widgetTypeLabel } from '../utils/widgetTypeLabel.js';
import {
  useWidgetPreviewSlideOutPortal,
  WIDGET_PREVIEW_SCROLL_CLASS,
  WIDGET_PREVIEW_SLIDEOUT_PROPS,
  WIDGET_PREVIEW_SLOT_CLASS,
} from './previewSlideOutShared.js';
import PlacementList from './PlacementList.jsx';
import PreviewSizeControl from './PreviewSizeControl.jsx';
import WidgetDetailPreview from './WidgetDetailPreview.jsx';

import { LayoutDashboard, LayoutGrid, Pencil, ShieldCheck } from 'lucide-react';

const DEFAULT_PREVIEW_SIZE = 'medium';

/**
 * Widget detail panel (CT-5188 / DS-4).
 *
 * A `SlideOut`, not a modal: it is a side panel, and `SlideOut` already owns the
 * backdrop, the scroll lock, the focus trap, `Esc` and focus restore — so there
 * is deliberately no `handleKeyDown`, no `FOCUSABLE` list and no `createPortal`
 * anywhere in this file.
 *
 * Presentational: the page owns which widget is open and what the two actions
 * do. `previewSize` is the one piece of state that belongs here; the page keys
 * this component on the open widget, so it starts at `medium` on every open
 * rather than remembering the last size.
 *
 * **No delete here.** The panel is where a widget is read before it is placed,
 * and a destructive action sat in it next to the two that build on it. Deleting
 * is still a row action, behind the list's own `⋮` menu and the same
 * confirm dialog — one step further from a panel someone opened to look.
 */
function WidgetDetailPanel({ open, widget, onClose, onEdit, onAddToDashboard }) {
  const { t } = useTranslation('dashboards');
  const [previewSize, setPreviewSize] = useState(DEFAULT_PREVIEW_SIZE);
  const portalTarget = useWidgetPreviewSlideOutPortal();

  // Plain locals rather than `widget?.x` inside each `useMemo`: the React
  // Compiler infers `widget.x` and then refuses to preserve the memoization
  // because the written dependency does not match what it inferred.
  const widgetType = widget?.type ?? '';
  const widgetStatus = widget?.status ?? '';
  const widgetDashboards = widget?.dashboards;
  // `null` — not `0` — when the backend reports no placements at all; the
  // section is then not rendered rather than claiming the widget is unused.
  const widgetUsedOnDashboards = widget?.usedOnDashboards ?? null;

  const handleEdit = useCallback(() => onEdit?.(widget?.id), [onEdit, widget?.id]);
  const handleAddToDashboard = useCallback(
    () => onAddToDashboard?.(widget),
    [onAddToDashboard, widget],
  );

  const typeLabel = useMemo(
    () => (widgetType ? widgetTypeLabel(widgetType, t) : ''),
    [t, widgetType],
  );

  // The prototype's own detail panel puts the status pill in the header row,
  // next to the title — not in the tags row below (which carries only type +
  // governance). `SlideOutHeader`'s `status` prop is the only way in without
  // forking the DS component.
  const statusLabel = useMemo(
    () => (widgetStatus ? t(`widgetLibrary.row.status.${widgetStatus}`) : ''),
    [t, widgetStatus],
  );

  // `null` when the type has no authored copy — the two sections then render
  // nothing at all rather than an empty heading (CT-5188 AC).
  const copyKeys = useMemo(() => widgetTypeCopyKeys(widgetType), [widgetType]);

  const dashboards = useMemo(() => widgetDashboards ?? [], [widgetDashboards]);

  const placementsTitle = useMemo(
    () => (widgetUsedOnDashboards == null
      ? ''
      : t('widgetLibrary.detail.usedOnDashboards', { count: widgetUsedOnDashboards })),
    [t, widgetUsedOnDashboards],
  );

  if (!widget) return null;

  const { icon } = widgetTypeIcon(widget.type);

  return (
    <SlideOut
      isOpen={open}
      onClose={onClose}
      ariaLabel={widget.name}
      portalTarget={portalTarget}
      {...WIDGET_PREVIEW_SLIDEOUT_PROPS}
    >
      <div className={WIDGET_PREVIEW_SLOT_CLASS}>
        <div className="flex flex-col gap-3 border-b border-edge px-6 pb-4 pt-6">
          <SlideOutHeader
            size="S"
            icon={icon}
            title={widget.name}
            subtitle={widget.source}
            status={statusLabel}
            showClose
            onClose={onClose}
          />
          <div className="flex flex-wrap items-center gap-2">
            {typeLabel ? (
              <Badge variant="secondary" size="small">
                {typeLabel}
              </Badge>
            ) : null}
            {widget.governed ? (
              <Badge variant="informative" radius="full" icon={ShieldCheck} size="small">
                {t('widgetLibrary.row.governed')}
              </Badge>
            ) : null}
          </div>
        </div>

        <div className={`${WIDGET_PREVIEW_SCROLL_CLASS} flex flex-col gap-5 px-6 py-5`}>
          <DrawerSection
            title={t('widgetLibrary.detail.preview')}
            rightContent={
              <PreviewSizeControl value={previewSize} onChange={setPreviewSize} />
            }
          >
            <WidgetDetailPreview widget={widget} size={previewSize} />
          </DrawerSection>

          {copyKeys ? (
            <>
              {/* `DrawerSection`'s `title` is a plain string with no icon slot, and
                  the prototype puts a small icon before `About` / `Best for` — so
                  these two skip `title` and build the same heading row by hand. */}
              <DrawerSection>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-content-faint">
                  <LayoutGrid size={12} aria-hidden />
                  {t('widgetLibrary.detail.about')}
                </div>
                <p className="m-0 mt-2 text-xs text-content-secondary">{t(copyKeys.about)}</p>
              </DrawerSection>
              <DrawerSection>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-content-faint">
                  <LayoutDashboard size={12} aria-hidden />
                  {t('widgetLibrary.detail.bestFor')}
                </div>
                <p className="m-0 mt-2 text-xs text-content-secondary">{t(copyKeys.bestFor)}</p>
              </DrawerSection>
            </>
          ) : null}

          {placementsTitle ? (
            <DrawerSection title={placementsTitle}>
              <PlacementList
                dashboards={dashboards}
                emptyLabel={t('widgetLibrary.detail.notPlaced')}
                fallbackLabel={t('widgetLibrary.detail.dashboardLabel')}
              />
            </DrawerSection>
          ) : null}
        </div>

        <div className="flex items-center gap-2 border-t border-edge px-6 py-4">
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" leftIcon={Pencil} onClick={handleEdit}>
              {t('widgetLibrary.detail.editFullConfig')}
            </Button>
            <Button variant="primary" leftIcon={LayoutDashboard} onClick={handleAddToDashboard}>
              {t('widgetLibrary.row.addToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    </SlideOut>
  );
}

WidgetDetailPanel.propTypes = {
  open: PropTypes.bool,
  widget: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    source: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    governed: PropTypes.bool,
    usedOnDashboards: PropTypes.number,
    dashboards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        entity: PropTypes.string,
      }),
    ),
  }),
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onAddToDashboard: PropTypes.func,
};

export default WidgetDetailPanel;
